import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
// UI Components
import { Row, Col, Form, Button, Card, Spinner } from "react-bootstrap";
// Styles & Assets
import "rc-slider/assets/index.css";
import "./Jobs.scss";
import "./jobsRedesign.scss";
import Select from "react-select";
import useTour from "../../components/tour/useTour";
import GuidedTour from "../../components/tour/GuidedTour";
import getJobsTourSteps from "./jobsTourSteps";
import { ReactComponent as FiSearch } from "../../assets/images/search-icon.svg";
import { ReactComponent as FiSliders } from "../../assets/images/filters-icon.svg";
// Components & Utils
import useCategories from "../community/hooks/useCategories";
import SimpleJobCard from "./components/JobCard/SimpleJobCard";
import NoIndexSEO from "../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../utils/seoConfig";
import { useLocation, useNavigate } from "react-router-dom";
import {
  checkMultipleBookmarks,
  getBookmarkJob,
  getEstimatedSalary,
  getJobFilter,
  getjobSearchResults,
  jobStatus,
  jobTracking,
  toggleBookmark,
} from "./../../redux/slices/jobsSlice";
import Slider from "rc-slider";
import { ReactComponent as CommingSoonIcon } from "../../assets/images/comming-soon.svg";
import {
  activityEnd,
  activityStart,
} from "../../redux/slices/trackActivitySlice";
import usePageTracking from "../../hooks/usePageTracking";
import { sessionId } from "../../redux/slices/cityComparisonSlice";
import { FiBookmark } from "react-icons/fi";
import { Bookmark, BookmarkCheck, UserRound } from "lucide-react";
import AppliedJobCard from "./components/JobCard/AppliedJobCard";
import {
  PROFILE_COMPLETION_FIELDS,
  getProfileCompletionPercent,
} from "../../utils/profileCompletion";

const PROFILE_COMPLETION_FIELDS_BY_KEY = Object.fromEntries(
  PROFILE_COMPLETION_FIELDS.map((field) => [field.key, field]),
);

// Priority-ordered: the first of these still incomplete is the one shown,
// since it's the field most likely to affect which jobs match this user.
const JOB_RELEVANT_PROFILE_NUDGES = [
  { key: "role", message: "Add your nursing role to see jobs matched to you." },
  {
    key: "experience",
    message: "Add your years of experience to see better-matched jobs.",
  },
  {
    key: "license_number",
    message: "Add your license number to see jobs you're eligible for.",
  },
  {
    key: "salary",
    message: "Add your preferred salary to see jobs that meet your expectations.",
  },
  { key: "city", message: "Add your city to see jobs near you." },
];

const JOB_TYPE_OPTIONS = [
  { value: "full-time", label: "Full-time" },
  { value: "part-time", label: "Part-time" },
  { value: "contract", label: "Contract" },
  { value: "per-diem", label: "Per-Diem" },
  { value: "temporary", label: "Temporary" },
  { value: "travel", label: "Travel" },
];

const SPECIALTY_TYPE_OPTION = [
  {
    value: "on-site",
    label: "On-Site",
  },
  {
    value: "remote",
    label: "Remote",
  },
  {
    value: "hybrid",
    label: "Hybrid",
  },
];

const sort_by = [
  { value: "salary", label: "Salary" },
  { value: "posted_date", label: "Posted Date" },
  { value: "apply_count_desc", label: "Most Clicked" },
  { value: "apply_count_asc", label: "Least Clicked" },
];
const SHIFT_TYPE_OPTION = [
  {
    value: "day",
    label: "Day",
  },
  {
    value: "evening",
    label: "Evening",
  },
  {
    value: "night",
    label: "Night",
  },
  {
    value: "flexible",
    label: "Flexible",
  },
  {
    value: "rotating",
    label: "Rotating",
  },
];

const INITIAL_FILTERS = {
  keyword: "",
  title: "",
  employer: "",
  location: "",
  state: "",
  taxonomySpecialty: "",
  taxonomySubSpecialty: "",
  nursingRole: "",
  page: "1",
  country: "US",
  date_posted: "week",
  workTypes: [],
  jobType: null,
  showOnlySalaries: false,
  specialties: [],
  shiftType: [],
  travelStaff: [],
  sort_by: [],
  radius: 60,
};

/* Skeleton placeholder for a job card — shown while the first page loads.
 * Mirrors the SimpleJobCard column classes so the grid doesn't reflow when
 * real data arrives. */
function JobCardSkeleton() {
  return (
    <Col xl={6} lg={6} md={6} sm={12} className="simple-job-card" aria-hidden="true">
      <div className="jsk-card">
        <div className="jsk-header">
          <div className="jsk jsk--avatar" />
          <div className="jsk-stack">
            <div className="jsk jsk--xs" />
            <div className="jsk jsk--md" />
          </div>
          <div className="jsk-tail">
            <div className="jsk jsk--pill" />
            <div className="jsk jsk--square" />
          </div>
        </div>
        <div className="jsk-row">
          <div className="jsk jsk--dot" />
          <div className="jsk jsk--sm" />
        </div>
        <div className="jsk jsk--lg" />
        <div className="jsk-row jsk-row--between">
          <div className="jsk jsk--xs" />
          <div className="jsk jsk--xs jsk--w20" />
        </div>
        <div className="jsk-grid">
          <div className="jsk-stack">
            <div className="jsk jsk--xxs" />
            <div className="jsk jsk--sm" />
          </div>
          <div className="jsk-stack">
            <div className="jsk jsk--xxs" />
            <div className="jsk jsk--sm" />
          </div>
          <div className="jsk-stack">
            <div className="jsk jsk--xxs" />
            <div className="jsk jsk--sm" />
          </div>
          <div className="jsk-stack">
            <div className="jsk jsk--xxs" />
            <div className="jsk jsk--sm" />
          </div>
        </div>
      </div>
    </Col>
  );
}

const Jobs = () => {
  // Guided tour — auto-starts on first visit, replayable from the header.
  const jobsTourSteps = useMemo(() => getJobsTourSteps(), []);
  const { isOpen: isTourOpen, finish: finishTour } = useTour("jobs");

  // Bridges the brief gap between a filter change and Redux `isLoading`
  // flipping to true — without it, the user sees a blank "0 cards / stale
  // count" frame after clicking Clear all or applying a filter.
  const [awaitingResults, setAwaitingResults] = useState(true);
  const loadingRef = useRef(false);
  const filterSheetRef = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const store = useSelector((state) => state.jobResults);

  const [isLoadMore, setIsLoadMore] = useState(false);
  const { categories, loading: categoriesLoading } = useCategories();
  const { jobStatusItems } = useSelector((state) => state.jobResults);

  // Get user and plan info early
  const user = useSelector((state) => state.auth?.user);
  const profileStore = useSelector((state) => state.profile);
  const userPlanSlug = user?.plan?.slug?.toLowerCase();

  // "Complete your profile" nudge shown above the results list — shows the
  // overall completion percentage plus the single highest-priority missing
  // field from JOB_RELEVANT_PROFILE_NUDGES as the actionable message.
  // Dismissal is in-memory only (not persisted) so the nudge reappears on
  // reload, re-login, or a fresh tab — not just once ever.
  const [profileNudgeDismissed, setProfileNudgeDismissed] = useState(false);
  const profileData = profileStore?.profileItem?.data;
  const profileCompletionPercent = getProfileCompletionPercent(profileData);
  const activeProfileNudge = profileData
    ? JOB_RELEVANT_PROFILE_NUDGES.find((nudge) => {
        const field = PROFILE_COMPLETION_FIELDS_BY_KEY[nudge.key];
        return field && !field.isComplete(profileData);
      })
    : null;
  const dismissProfileNudge = () => {
    setProfileNudgeDismissed(true);
  };

  const hasUsedProTrial =
    user?.has_used_pro_trial ??
    profileStore?.profileItem?.data?.has_used_pro_trial ??
    false;
  // --- State ---
  const [jobs, setJobs] = useState([]);
  // const [hasMore, setHasMore] = useState(true);
  const [removedBookmarks, setRemovedBookmarks] = useState(0);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [searchTerm, setSearchTerm] = useState(filters.query || "");
  const [showFilters, setShowFilters] = useState(false);
  const skipTrackingRef = useRef(false);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [activeTab, setActiveTab] = useState("jobs");
  const [showScrollPopup, setShowScrollPopup] = useState(false);
  const [hasClosedPopup, setHasClosedPopup] = useState(false);
  const lastTrackedState = useRef({
    pageLoaded: false,
    keyword: "",
    employer: "",
    location: "",
    jobType: "",
    shiftType: "",
    specialty: "",
    workModel: "",
    sort: "",
  });

  const prevFiltersRef = useRef(filters);

  // Track Activity
  const {
    jobFilterItems,
    JobsResults,
    isLoading,
    isError,
    errorMessage,
    getBookmarkJobItems,
  } = useSelector(
    (state) => state.jobResults, // ✅ CORRECT KEY
  );
  // const LIMIT = 10;
  // const jobsData = JobsResults?.data?.data || [];
  const LIMIT = 10;

  // const jobsData = showBookmarks
  //   ? getBookmarkJobItems?.data || []
  //   : JobsResults?.data?.data || [];
  const jobsData =
    activeTab === "applied"
      ? jobStatusItems?.applications || []
      : showBookmarks
        ? getBookmarkJobItems?.data || []
        : JobsResults?.data?.data || [];

  // const hasMore = jobsData.length === LIMIT;
  // const hasMore = activeTab === "applied" ? false : jobsData.length === LIMIT;
  const total = JobsResults?.data?.total || 0;

  const hasMore = activeTab === "applied" ? false : jobs.length < total;

  // const totalJobs = showBookmarks
  //   ? (getBookmarkJobItems?.total || 0) - removedBookmarks
  //   : JobsResults?.data?.total ?? 0;
  const totalJobs =
    activeTab === "applied"
      ? jobStatusItems?.pagination?.total || 0
      : showBookmarks
        ? (getBookmarkJobItems?.total || 0) - removedBookmarks
        : JobsResults?.data?.total ?? 0;

  // Featured jobs: the API returns featured jobs first and exposes the total
  // number of featured jobs matching the current filters via `featured_total`.
  // We split the loaded `jobs` list into a featured section (shown first) and
  // the remaining regular jobs. When there are no featured jobs, the page
  // renders exactly as before.
  const featuredTotal =
    activeTab === "jobs" && !showBookmarks
      ? JobsResults?.data?.featured_total || 0
      : 0;
  const hasFeaturedSection = featuredTotal > 0;
  const featuredJobs = hasFeaturedSection
    ? jobs.filter((job) => job.is_featured)
    : [];
  const regularJobs = hasFeaturedSection
    ? jobs.filter((job) => !job.is_featured)
    : jobs;
  // `total` from the API counts every matching job (featured + regular), so the
  // "Showing N Results" heading for the regular section must subtract the
  // featured jobs to avoid double-counting them.
  const regularTotal = Math.max(0, totalJobs - featuredTotal);

  // const noResults =
  //   !isLoading &&
  //   !isError &&
  //   Number(filters.page) === 1 &&
  //   jobsData.length === 0;
  const noResults =
    !isLoading &&
    !isError &&
    Number(filters.page) === 1 &&
    (activeTab === "applied"
      ? (jobStatusItems?.applications?.length || 0) === 0
      : jobsData.length === 0);
  usePageTracking("JOBS");

  // Scroll popup effect
  useEffect(() => {
    const handleScrollPopup = () => {
      // Don't show popup if user has already closed it
      if (hasClosedPopup) return;

      // Only free users should see this popup; skip it for any non-free
      // (paid) plan — plus / pro / pro-trial / pro-newsletter / free-trial.
      if (userPlanSlug && userPlanSlug !== "free") return;

      // Don't pop the upgrade sheet while the guided tour drives scroll.
      if (document.body.classList.contains("mmp-tour-active")) return;

      const scrollPosition = window.scrollY;
      const pageHeight = document.body.scrollHeight - window.innerHeight;

      if (scrollPosition / pageHeight > 0.35 && !showScrollPopup) {
        setShowScrollPopup(true);
      }
    };

    window.addEventListener("scroll", handleScrollPopup);
    return () => window.removeEventListener("scroll", handleScrollPopup);
  }, [showScrollPopup, hasClosedPopup, userPlanSlug]);

  useEffect(() => {
    const payload = {
      page_name: "JOBS",
      platform: "web",
    };

    dispatch(activityStart(payload));
  }, [dispatch]);

  // Lock page scroll + drag-to-close for the mobile filter bottom sheet
  useEffect(() => {
    if (!showFilters) return undefined;
    const el = filterSheetRef.current;
    if (!el) return undefined;

    const isDesktop =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(min-width: 992px)").matches;

    let prevBodyOverflow;
    let prevHtmlOverscroll;
    if (!isDesktop) {
      prevBodyOverflow = document.body.style.overflow;
      prevHtmlOverscroll = document.documentElement.style.overscrollBehavior;
      document.body.style.overflow = "hidden";
      document.documentElement.style.overscrollBehavior = "none";
    }

    if (isDesktop) {
      return () => {
        if (prevBodyOverflow !== undefined) {
          document.body.style.overflow = prevBodyOverflow;
        }
        if (prevHtmlOverscroll !== undefined) {
          document.documentElement.style.overscrollBehavior = prevHtmlOverscroll;
        }
      };
    }

    let startY = 0;
    let currentDelta = 0;
    let dragging = false;

    const onTouchStart = (e) => {
      const touch = e.touches[0];
      const rect = el.getBoundingClientRect();
      if (touch.clientY - rect.top > 80) return;
      if (el.scrollTop > 0) return;
      startY = touch.clientY;
      currentDelta = 0;
      dragging = true;
      el.style.transition = "none";
    };

    const onTouchMove = (e) => {
      if (!dragging) return;
      const delta = e.touches[0].clientY - startY;
      if (delta > 0) {
        if (e.cancelable) e.preventDefault();
        currentDelta = delta;
        el.style.transform = `translateY(${delta}px)`;
      }
    };

    const onTouchEnd = () => {
      if (!dragging) return;
      dragging = false;
      el.style.transition = "";
      if (currentDelta > 100) {
        setShowFilters(false);
      } else {
        el.style.transform = "";
      }
      currentDelta = 0;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      el.style.transform = "";
      el.style.transition = "";
      if (prevBodyOverflow !== undefined) {
        document.body.style.overflow = prevBodyOverflow;
      }
      if (prevHtmlOverscroll !== undefined) {
        document.documentElement.style.overscrollBehavior = prevHtmlOverscroll;
      }
    };
  }, [showFilters]);

  useEffect(() => {
    return () => {
      dispatch(
        activityEnd({
          page_name: "JOBS",
        }),
      );
    };
  }, [dispatch]);

  

  const handleSearchBox = (value) => {
    setSearchTerm(value);
  };
  const titleOptions =
    jobFilterItems?.data?.titles?.map((title) => ({
      value: title,
      label: title,
    })) || [];

  // const employerOptions =
  //   jobFilterItems?.data?.employers?.map((emp) => ({
  //     value: emp,
  //     label: emp,
  //   })) || [];
  const employerOptions =
    jobFilterItems?.data?.employers?.map((emp) => ({
      value: emp.trim(),
      label: emp.trim(),
    })) || [];
  const locationOptions =
    jobFilterItems?.data?.locations?.map((loc) => ({
      value: loc,
      label: loc,
    })) || [];
  const stateOptions =
    jobFilterItems?.data?.states?.map((st) => ({
      value: st,
      label: st,
    })) || [];
  const taxonomySpecialtyOptions =
    jobFilterItems?.data?.taxonomySpecialties?.map((spec) => ({
      value: spec,
      label: spec,
    })) || [];
  const taxonomySubSpecialtyOptions =
    jobFilterItems?.data?.taxonomySubSpecialties?.map((sub) => ({
      value: sub,
      label: sub,
    })) || [];
  const nursingRoleOptions =
    jobFilterItems?.data?.nursingRoles?.map((role) => ({
      value: role.id,
      label: role.role_title,
    })) || [];

  // useEffect(() => {
  //   if (showBookmarks) {
  //     // Fetch bookmarked jobs
  //     dispatch(getBookmarkJob({ page: Number(filters.page), limit: 10 }));
  //   } else {
  //     // Fetch regular jobs
  //     const params = {
  //       page: Number(filters.page),
  //       limit: 10,
  //     };

  //     if (filters.keyword?.trim()) {
  //       params.keyword = filters.keyword.trim();
  //     }

  //     if (filters.title?.trim()) {
  //       params.title = filters.title.trim();
  //     }

  //     if (filters.employer?.trim()) {
  //       params.employer = filters.employer.trim();
  //     }

  //     if (filters.location?.trim()) {
  //       params.location = filters.location.trim();
  //     }

  //     if (filters.workTypes?.length > 0) {
  //       params.job_type = filters.workTypes[0];
  //     }

  //     if (filters.shiftType?.length > 0) {
  //       params.shift_type = filters.shiftType[0];
  //     }

  //     if (filters.specialties?.length > 0) {
  //       params.work_model = filters.specialties[0];
  //     }

  //     if (filters.sort_by?.length > 0) {
  //       params.sort_by = filters.sort_by[0];
  //     }
  //     

  //     dispatch(getjobSearchResults(params));
  //   }
  // }, [
  //   showBookmarks,
  //   filters.page,
  //   filters.keyword,
  //   filters.title,
  //   filters.employer,
  //   filters.location,
  //   filters.workTypes,
  //   filters.shiftType,
  //   filters.specialties,
  //   filters.sort_by,
  //   dispatch,
  // ]);
  useEffect(() => {
    // ✅ ADD THIS LINE
    if (activeTab === "applied") return;

    // Mark "we are now waiting for results" synchronously, before the
    // dispatch — covers the render frame between this effect firing and
    // Redux's isLoading flipping true.
    setAwaitingResults(true);

    if (showBookmarks) {
      // Fetch bookmarked jobs
      dispatch(getBookmarkJob({ page: Number(filters.page), limit: 10 }));
    } else {
      // Fetch regular jobs
      const params = {
        page: Number(filters.page),
        limit: 10,
      };

      if (filters.keyword?.trim()) {
        params.keyword = filters.keyword.trim();
      }

      if (filters.title?.trim()) {
        params.title = filters.title.trim();
      }

      if (filters.employer?.trim()) {
        params.employer = filters.employer.trim();
      }

      if (filters.location?.trim()) {
        params.location = filters.location.trim();
      }

      if (filters.state?.trim()) {
        params.state = filters.state.trim();
      }

      if (filters.taxonomySpecialty?.trim()) {
        params.taxonomy_specialty = filters.taxonomySpecialty.trim();
      }

      if (filters.taxonomySubSpecialty?.trim()) {
        params.taxonomy_sub_specialty = filters.taxonomySubSpecialty.trim();
      }

      if (filters.nursingRole) {
        params.describe_role_id = filters.nursingRole;
      }

      if (filters.workTypes?.length > 0) {
        params.job_type = filters.workTypes[0];
      }

      if (filters.shiftType?.length > 0) {
        params.shift_type = filters.shiftType[0];
      }

      if (filters.specialties?.length > 0) {
        params.work_model = filters.specialties[0];
      }

      if (filters.sort_by?.length > 0) {
        const selectedSort = filters.sort_by[0];
        if (selectedSort === "apply_count_desc") {
          params.sort_by = "apply_count";
          params.sort_order = "desc";
        } else if (selectedSort === "apply_count_asc") {
          params.sort_by = "apply_count";
          params.sort_order = "asc";
        } else {
          params.sort_by = selectedSort;
        }
      }

      dispatch(getjobSearchResults(params));
    }
  }, [
    activeTab, // ✅ add this dependency
    showBookmarks,
    filters.page,
    filters.keyword,
    filters.title,
    filters.employer,
    filters.location,
    filters.state,
    filters.taxonomySpecialty,
    filters.taxonomySubSpecialty,
    filters.nursingRole,
    filters.workTypes,
    filters.shiftType,
    filters.specialties,
    filters.sort_by,
    dispatch,
  ]);

  useEffect(() => {
    dispatch(getJobFilter());
  }, [dispatch]);

  useEffect(() => {
    loadingRef.current = isLoading;
    // Response landed → we're no longer waiting.
    if (!isLoading) setAwaitingResults(false);
  }, [isLoading]);

  // const loadMoreJobs = useCallback(() => {
  //   if (!hasMore || loadingRef.current || isError) return;

  //   loadingRef.current = true; // 🔥 BLOCK immediately

  //   setIsLoadMore(true);

  //   setFilters((prev) => ({
  //     ...prev,
  //     page: Number(prev.page) + 1,
  //   }));
  // }, [hasMore, isError]);

  const loadMoreJobs = useCallback(() => {
    if (!hasMore || loadingRef.current || isError || isLoading) return;

    loadingRef.current = true;

    setIsLoadMore(true);

    setFilters((prev) => ({
      ...prev,
      page: Number(prev.page) + 1,
    }));
  }, [hasMore, isError, isLoading]);
  useEffect(() => {
    if (showBookmarks) {
      if (!getBookmarkJobItems?.data) return;

      const newJobs = getBookmarkJobItems.data || [];

      if (Number(filters.page) === 1) {
        setJobs(newJobs);
      } else {
        setJobs((prev) => {
          const merged = [...prev, ...newJobs];
          const unique = Array.from(
            new Map(merged.map((job) => [job.id, job])).values(),
          );
          return unique;
        });
      }
    } else {
      if (!JobsResults?.data?.data) return;

      const newJobs = JobsResults.data.data;

      if (Number(filters.page) === 1) {
        setJobs(newJobs);
      } else {
        setJobs((prev) => {
          const merged = [...prev, ...newJobs];
          const unique = Array.from(
            new Map(merged.map((job) => [job.id, job])).values(),
          );
          return unique;
        });
      }
    }

    loadingRef.current = false; // 🔥 UNLOCK
    setIsLoadMore(false);
  }, [JobsResults, getBookmarkJobItems, filters.page, showBookmarks]);

  useEffect(() => {
    let timeout = null;
    const handleScroll = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (loadingRef.current) return;
        // Pause pagination while the guided tour drives the scroll — otherwise
        // scrolling to a later step keeps loading more jobs, growing the page.
        if (document.body.classList.contains("mmp-tour-active")) return;
        const scrollTop = window.scrollY;
        const height = window.innerHeight;
        const fullHeight = document.body.scrollHeight;
        // if (scrollTop + height >= fullHeight - 400) {
        if (scrollTop + height >= fullHeight - 200) {
          loadMoreJobs();
        }
      }, 200);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadMoreJobs]);

  useEffect(() => {
    // Jobs tab only — the Applied tab drives its own search effect below.
    if (activeTab === "applied") return undefined;

    const trimmed = searchTerm.trim();

    if (trimmed === "" && filters.keyword === "") return undefined;

    const delayDebounce = setTimeout(() => {
      setJobs([]);
      setFilters((prev) => ({
        ...prev,
        keyword: trimmed,
        page: "1",
      }));
    }, 500);

    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, activeTab]);

  const handleWorkTypeChange = (workType) => {
    setJobs([]);
    setFilters((prev) => ({
      ...prev,
      page: "1",
      workTypes: prev.workTypes[0] === workType ? [] : [workType],
    }));
  };

  const clearAllFilters = () => {
    skipTrackingRef.current = true;
    setJobs([]);

    // reset ALL filters
    setFilters({
      ...INITIAL_FILTERS,
      keyword: "",
      title: "",
      employer: "",
      location: "",
      state: "",
      taxonomySpecialty: "",
      taxonomySubSpecialty: "",
      nursingRole: "",
      page: "1",
    });

    // reset search input
    setSearchTerm("");
  };

  // Switch tabs and reset the shared search box so a term typed on one tab
  // doesn't carry over to the other. No-op when the tab is already active.
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setSearchTerm("");
    setActiveTab(tab);
  };

  // True when at least one user-controllable filter is active. Mirrors
  // exactly what clearAllFilters() resets, so the "Clear all" button is
  // only enabled when there's actually something to clear. The baseline
  // fields kept by clearAllFilters (country, date_posted, radius, page)
  // are intentionally excluded — they aren't user-applied filters.
  const hasActiveFilters =
    Boolean(searchTerm) ||
    Boolean(filters.keyword) ||
    Boolean(filters.title) ||
    Boolean(filters.employer) ||
    Boolean(filters.location) ||
    Boolean(filters.state) ||
    Boolean(filters.taxonomySpecialty) ||
    Boolean(filters.taxonomySubSpecialty) ||
    Boolean(filters.nursingRole) ||
    filters.jobType != null ||
    filters.showOnlySalaries ||
    filters.workTypes.length > 0 ||
    filters.specialties.length > 0 ||
    filters.shiftType.length > 0 ||
    filters.travelStaff.length > 0 ||
    filters.sort_by.length > 0;

  const handleWorkMode = (mode) => {
    setJobs([]);
    setFilters((prev) => ({
      ...prev,
      page: "1",
      specialties: prev.specialties[0] === mode ? [] : [mode],
    }));
  };

  const handleShiftType = (shift) => {
    setJobs([]);
    setFilters((prev) => ({
      ...prev,
      page: "1",
      shiftType: prev.shiftType[0] === shift ? [] : [shift],
    }));
  };

  const handleSortBy = (value) => {
    setJobs([]);
    setFilters((prev) => ({
      ...prev,
      page: "1",
      sort_by: prev.sort_by[0] === value ? [] : [value],
    }));
  };

  useEffect(() => {
    if (!user) {
      sessionStorage.removeItem("comparison_session_id");
      sessionStorage.removeItem("session_expiry");

    }
  }, [user]);
  useEffect(() => {
    if (!user?.id) return;

    const existingSession = sessionStorage.getItem("comparison_session_id");
    const expiry = sessionStorage.getItem("session_expiry");

    // 🔥 Check if valid session exists
    if (existingSession && expiry && Date.now() < Number(expiry)) {

      return;
    }

    // const token = localStorage.getItem("authToken");

    // dispatch(
    //   sessionId({
    //     user_id: user.id,
    //     access_token: token,
    //   }),
    dispatch(sessionId()).then((res) => {
      const data = res.payload?.data;

      if (data?.session_id) {
        sessionStorage.setItem("comparison_session_id", data.session_id);

        // 🔥 store expiry (important)
        const expiryTime = Date.now() + data.expires_in * 1000;
        sessionStorage.setItem("session_expiry", expiryTime);

      }
    });
  }, [user]);

  useEffect(() => {
    if (skipTrackingRef.current) {
      skipTrackingRef.current = false;
      prevFiltersRef.current = filters;
      return; // 🚀 stop everything
    }
    if (!JobsResults?.data) return;

    const session_id = sessionStorage.getItem("comparison_session_id");
    if (!session_id) return;

    const total = JobsResults.data.total ?? 0;

    const prevFilters = prevFiltersRef.current;
    const currentFilters = filters;

    const payload = {
      session_id,
      results_count: total,
      timestamp: new Date().toISOString(),
    };

    //PAGE LOAD
    if (!lastTrackedState.current.pageLoaded) {
      lastTrackedState.current.pageLoaded = true;

      dispatch(
        jobTracking({
          ...payload,
          event_type: "job_viewed",
        }),
      );

      prevFiltersRef.current = currentFilters;
      return;
    }

    // SEARCH
    if (
      prevFilters.keyword !== currentFilters.keyword &&
      currentFilters.keyword?.trim() !== ""
    ) {
      dispatch(
        jobTracking({
          ...payload,
          event_type: "search_initiated",
        }),
      );

      prevFiltersRef.current = currentFilters;
      return;
    }

    if (
      prevFilters.employer !== currentFilters.employer &&
      currentFilters.employer
    ) {
      dispatch(
        jobTracking({
          ...payload,
          event_type: "filter_applied",
          employer: currentFilters.employer,
        }),
      );

      prevFiltersRef.current = currentFilters;
      return;
    }
    // FILTER CHANGE
    const filterChanged =
      // prevFilters.employer !== currentFilters.employer ||
      prevFilters.location !== currentFilters.location ||
      prevFilters.state !== currentFilters.state ||
      prevFilters.title !== currentFilters.title ||
      JSON.stringify(prevFilters.workTypes) !==
      JSON.stringify(currentFilters.workTypes) ||
      JSON.stringify(prevFilters.shiftType) !==
      JSON.stringify(currentFilters.shiftType) ||
      JSON.stringify(prevFilters.specialties) !==
      JSON.stringify(currentFilters.specialties);

    // if (filterChanged) {
    if (
      filterChanged &&
      (currentFilters.workTypes.length > 0 ||
        currentFilters.shiftType.length > 0 ||
        currentFilters.specialties.length > 0 ||
        currentFilters.title ||
        currentFilters.location ||
        currentFilters.state)
    ) {
      // Split location safely
      let city = null;
      let splitState = null;

      if (currentFilters.location) {
        const parts = currentFilters.location.split(",").map((i) => i.trim());
        city = parts[0] || null;
        splitState = parts[1] || null;
      }

      // Explicit state filter takes precedence over one parsed out of location
      const state = currentFilters.state || splitState;

      // Build only allowed fields
      const filterPayload = {
        ...payload,
        event_type: "filter_applied",
      };

      // if (currentFilters.employer)
      //   filterPayload.employer = currentFilters.employer;

      if (currentFilters.title) filterPayload.specialty = currentFilters.title;

      if (currentFilters.workTypes?.[0])
        filterPayload.job_type = currentFilters.workTypes[0];

      if (currentFilters.shiftType?.[0])
        filterPayload.shift_type = currentFilters.shiftType[0];

      if (currentFilters.specialties?.[0])
        filterPayload.work_model = currentFilters.specialties[0];

      if (city) filterPayload.location_city = city;
      if (state) filterPayload.location_state = state;

      dispatch(jobTracking(filterPayload));

      prevFiltersRef.current = currentFilters;
      return;
    }

    //  SORT CHANGE

    if (
      JSON.stringify(prevFilters.sort_by) !==
      JSON.stringify(currentFilters.sort_by)
    ) {
      dispatch(
        jobTracking({
          ...payload,
          event_type: "sort_changed",
          sort_method: currentFilters.sort_by?.[0],
        }),
      );

      prevFiltersRef.current = currentFilters;
    }
  }, [JobsResults, dispatch]);
  useEffect(() => {
    if (showBookmarks) {
      // For bookmarks, check the bookmarked jobs
      if (!getBookmarkJobItems?.data) return;
      const jobIds = getBookmarkJobItems.data.map((job) => job.id);
      if (jobIds.length > 0) {
        dispatch(checkMultipleBookmarks({ job_ids: jobIds }));
      }
    } else {
      // For regular jobs
      if (!JobsResults?.data?.data) return;
      const newJobs = JobsResults.data.data;
      const jobIds = newJobs.map((job) => job.id);
      if (jobIds.length > 0) {
        dispatch(checkMultipleBookmarks({ job_ids: jobIds }));
      }
    }
  }, [JobsResults, getBookmarkJobItems, showBookmarks, dispatch]);
  const handleBookmarkToggle = () => {
    setShowBookmarks(!showBookmarks);
    setJobs([]);
    setFilters((prev) => ({
      ...prev,
      page: "1",
    }));
  };

  // Listen for bookmark removal events
  useEffect(() => {
    const handleRemoveBookmark = (event) => {
      const jobId = event.detail;
      setJobs((prevJobs) => prevJobs.filter((job) => job.id !== jobId));
      setRemovedBookmarks((prev) => prev + 1);
    };

    window.addEventListener("removeBookmarkedJob", handleRemoveBookmark);

    return () => {
      window.removeEventListener("removeBookmarkedJob", handleRemoveBookmark);
    };
  }, []);
  useEffect(() => {
    if (showBookmarks) {
      setRemovedBookmarks(0);
    }
  }, [showBookmarks]);

  useEffect(() => {
    if (!JobsResults?.data?.data) return;

  }, [JobsResults]);
  useEffect(() => {
    if (activeTab !== "applied") return;

    const applications = jobStatusItems?.applications || [];

    // Transform applied jobs to match SimpleJobCard structure
    const appliedJobs = applications.map((app) => {
      const job = app.job;
      return {
        ...job,
        // Add application metadata for display
        application_id: app.application_id,
        application_status: app.status,
        qualified: app.qualified,
        disqualify_reason: app.disqualify_reason,
        match_score: app.match_score,
        readiness_percent: app.readiness_percent,
        applied_at: app.applied_at,
        interview_time: app.interview_time,
      };
    });

    setJobs(appliedJobs);
  }, [jobStatusItems, activeTab]);
  // Applied tab fetch — refetches on tab open and as the user searches
  // (by job title / hospital). Debounced while typing; fires immediately
  // when the term is empty (e.g. on tab open or after a reset).
  useEffect(() => {
    if (activeTab !== "applied") return undefined;

    const term = searchTerm.trim();
    const delay = term ? 500 : 0;

    const t = setTimeout(() => {
      dispatch(
        jobStatus({
          page: 1,
          limit: 10,
          ...(term ? { search: term } : {}),
        }),
      );
    }, delay);

    return () => clearTimeout(t);
  }, [activeTab, searchTerm, dispatch]);
  useEffect(() => {
    if (location.state?.tab === "applied") {
      setActiveTab("applied");
    }
  }, [location.state]);
  // useEffect(() => {
  //   if (activeTab !== "jobs") return;

  //   if (!JobsResults?.data?.data) return;

  //   setJobs(JobsResults.data.data);
  // }, [JobsResults, activeTab]);
  const activeFilterCount =
    (filters.title ? 1 : 0) +
    (filters.employer ? 1 : 0) +
    (filters.location ? 1 : 0) +
    (filters.state ? 1 : 0) +
    (filters.taxonomySpecialty ? 1 : 0) +
    (filters.taxonomySubSpecialty ? 1 : 0) +
    (filters.nursingRole ? 1 : 0) +
    (filters.workTypes?.length || 0) +
    (filters.shiftType?.length || 0) +
    (filters.specialties?.length || 0) +
    (filters.sort_by?.length || 0);

  return (
    <div className="jobs-page-content mmp-jobs-page">
      <NoIndexSEO title={seoConfig.jobs.title} />

      {activeProfileNudge && !profileNudgeDismissed && (
        <div
          className="jobs-profile-nudge"
          role="button"
          tabIndex={0}
          onClick={() => navigate("/profile")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate("/profile");
            }
          }}
        >
          <div className="jobs-profile-nudge-icon">
            <UserRound />
          </div>
          <div className="jobs-profile-nudge-body">
            <div className="jobs-profile-nudge-head">
              <span className="jobs-profile-nudge-eyebrow">
                Complete your profile
              </span>
              <span className="jobs-profile-nudge-pct">
                {profileCompletionPercent}%
              </span>
            </div>
            <div className="jobs-profile-nudge-bar-track">
              <div
                className="jobs-profile-nudge-bar-fill"
                style={{ width: `${profileCompletionPercent}%` }}
              />
            </div>
            <span className="jobs-profile-nudge-text">
              {activeProfileNudge.message}
            </span>
          </div>
          <button
            type="button"
            className="jobs-profile-nudge-close"
            aria-label="Dismiss"
            onClick={(e) => {
              e.stopPropagation();
              dismissProfileNudge();
            }}
          >
            ×
          </button>
        </div>
      )}

      <div className="jobs-page-heading">
        <h1>Jobs</h1>
        <p>Real take-home pay for every listing</p>
      </div>

      {showFilters && (
        <div
          className="mmp-filter-backdrop d-lg-none"
          onClick={() => setShowFilters(false)}
          role="presentation"
        />
      )}

      <Row>
        {/*  lg={4} xl={3} */}
        <div
          ref={filterSheetRef}
          className={`filter-sidebar ${showFilters ? "show" : ""}`}
          data-tour="jobs-filter"
        >
          <Card className="filter-container mb-0">
            <div className="inner-sub-sub-title">
              <div className="inner-sub-title">Filter</div>
              <div className="btn-clear-toggle">
                <Button
                  variant="link"
                  size="sm"
                  onClick={clearAllFilters}
                  className="clear-all"
                  disabled={!hasActiveFilters}
                >
                  Clear all
                </Button>
                <button
                  className="filter-toggle-btn"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <FiSliders />
                </button>
              </div>
            </div>

            <div className={`filter ${showFilters ? "open" : "closed"}`}>
              <div className=" dash-filter">
                <Form.Group className="form-group">
                  <Form.Label className="col-label">Filter by Nursing Role</Form.Label>

                  <Select
                    aria-label="Filter by nursing role"
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    options={nursingRoleOptions}
                    value={
                      filters.nursingRole
                        ? nursingRoleOptions.find((opt) => opt.value === filters.nursingRole) || null
                        : null
                    }
                    onChange={(option) => {
                      setJobs([]);
                      setFilters((prev) => ({
                        ...prev,
                        page: "1",
                        nursingRole: option?.value || "",
                      }));
                    }}
                    isClearable
                    placeholder="Select Nursing Role"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      menu: (base) => ({
                        ...base,
                        borderRadius: "15px",
                        marginTop: "8px",
                        backgroundColor: "var(--mmp-surface)",
                        padding: "6px",
                        zIndex: "1000",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--mmp-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--mmp-text-secondary)",
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: "13px",
                        lineHeight: 1.3,
                        borderRadius: "8px",
                        padding: "8px 10px",
                        backgroundColor: state.isFocused ? "var(--mmp-hover-bg)" : "var(--mmp-surface)",
                        color: "var(--mmp-text-primary)",
                        cursor: "pointer",
                        "&:active": {
                          backgroundColor: "var(--mmp-hover-bg)",
                        },
                      }),
                    }}
                  />
                </Form.Group>
              </div>
              <div className=" dash-filter">
                <Form.Group className="form-group">
                  <Form.Label className="col-label">
                    Filter by Specialty
                  </Form.Label>

                  <Select
                    aria-label="Filter by specialty"
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    options={taxonomySpecialtyOptions}
                    value={
                      filters.taxonomySpecialty
                        ? { value: filters.taxonomySpecialty, label: filters.taxonomySpecialty }
                        : null
                    }
                    onChange={(option) => {
                      setJobs([]);
                      setFilters((prev) => ({
                        ...prev,
                        page: "1",
                        taxonomySpecialty: option?.value || "",
                      }));
                    }}
                    isClearable
                    placeholder="Select Specialty"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      menu: (base) => ({
                        ...base,
                        borderRadius: "15px",
                        marginTop: "8px",
                        backgroundColor: "var(--mmp-surface)",
                        padding: "6px",
                        zIndex: "1000",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--mmp-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--mmp-text-secondary)",
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: "13px",
                        lineHeight: 1.3,
                        borderRadius: "8px",
                        padding: "8px 10px",
                        backgroundColor: state.isFocused ? "var(--mmp-hover-bg)" : "var(--mmp-surface)",
                        color: "var(--mmp-text-primary)",
                        cursor: "pointer",
                        "&:active": {
                          backgroundColor: "var(--mmp-hover-bg)",
                        },
                      }),
                    }}
                  />
                </Form.Group>
              </div>
              <div className=" dash-filter">
                <Form.Group className="form-group">
                  <Form.Label className="col-label">
                    Filter by Sub-Specialty
                  </Form.Label>

                  <Select
                    aria-label="Filter by sub-specialty"
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    options={taxonomySubSpecialtyOptions}
                    value={
                      filters.taxonomySubSpecialty
                        ? { value: filters.taxonomySubSpecialty, label: filters.taxonomySubSpecialty }
                        : null
                    }
                    onChange={(option) => {
                      setJobs([]);
                      setFilters((prev) => ({
                        ...prev,
                        page: "1",
                        taxonomySubSpecialty: option?.value || "",
                      }));
                    }}
                    isClearable
                    placeholder="Select Sub-Specialty"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      menu: (base) => ({
                        ...base,
                        borderRadius: "15px",
                        marginTop: "8px",
                        backgroundColor: "var(--mmp-surface)",
                        padding: "6px",
                        zIndex: "1000",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--mmp-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--mmp-text-secondary)",
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: "13px",
                        lineHeight: 1.3,
                        borderRadius: "8px",
                        padding: "8px 10px",
                        backgroundColor: state.isFocused ? "var(--mmp-hover-bg)" : "var(--mmp-surface)",
                        color: "var(--mmp-text-primary)",
                        cursor: "pointer",
                        "&:active": {
                          backgroundColor: "var(--mmp-hover-bg)",
                        },
                      }),
                    }}
                  />
                </Form.Group>
              </div>
              <div className=" dash-filter">
                <Form.Group className="form-group">
                  <Form.Label className="col-label">Filter by Job Title</Form.Label>

                  <Select
                    aria-label="Filter by job title"
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    options={titleOptions}
                    value={
                      filters.title
                        ? { value: filters.title, label: filters.title }
                        : null
                    }
                    onChange={(option) => {
                      setJobs([]);
                      setFilters((prev) => ({
                        ...prev,
                        page: "1",
                        title: option?.value || "",
                      }));
                    }}
                    isClearable
                    placeholder="Select Role"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      menu: (base) => ({
                        ...base,
                        borderRadius: "15px",
                        marginTop: "8px",
                        backgroundColor: "var(--mmp-surface)",
                        padding: "6px",
                        zIndex: "1000",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--mmp-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--mmp-text-secondary)",
                      }),
                      option: (base, state) => ({
                        ...base,
                        // Match the trigger control font size (13px) so the
                        // portalled menu doesn't inherit the body's 16px.
                        fontSize: "13px",
                        lineHeight: 1.3,
                        borderRadius: "8px",
                        padding: "8px 10px",
                        backgroundColor: state.isFocused ? "var(--mmp-hover-bg)" : "var(--mmp-surface)",
                        color: "var(--mmp-text-primary)",
                        cursor: "pointer",
                        "&:active": {
                          backgroundColor: "var(--mmp-hover-bg)",
                        },
                      }),
                    }}
                  />
                </Form.Group>
              </div>
              <div className=" dash-filter">
                <Form.Group className="form-group">
                  <Form.Label className="col-label">
                    Filter by Employer
                  </Form.Label>

                  <Select
                    aria-label="Filter by role"
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    options={employerOptions}
                    value={
                      filters.employer
                        ? { value: filters.employer, label: filters.employer }
                        : null
                    }
                    onChange={(option) => {
                      setJobs([]);
                      setFilters((prev) => ({
                        ...prev,
                        page: "1",
                        employer: option?.value || "",
                      }));
                    }}
                    isClearable
                    placeholder="Select Employer"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      menu: (base) => ({
                        ...base,
                        borderRadius: "15px",
                        marginTop: "8px",
                        backgroundColor: "var(--mmp-surface)",
                        padding: "6px",
                        zIndex: "1000",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--mmp-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--mmp-text-secondary)",
                      }),
                      option: (base, state) => ({
                        ...base,
                        // Match the trigger control font size (13px) so the
                        // portalled menu doesn't inherit the body's 16px.
                        fontSize: "13px",
                        lineHeight: 1.3,
                        borderRadius: "8px",
                        padding: "8px 10px",
                        backgroundColor: state.isFocused ? "var(--mmp-hover-bg)" : "var(--mmp-surface)",
                        color: "var(--mmp-text-primary)",
                        cursor: "pointer",
                        "&:active": {
                          backgroundColor: "var(--mmp-hover-bg)",
                        },
                      }),
                    }}
                  />
                </Form.Group>
              </div>
              <div className=" dash-filter">
                <Form.Group className="form-group">
                  <Form.Label className="col-label">
                    Filter by Location
                  </Form.Label>

                  <Select
                    aria-label="Filter by role"
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    options={locationOptions}
                    value={
                      filters.location
                        ? { value: filters.location, label: filters.location }
                        : null
                    }
                    onChange={(option) => {
                      setJobs([]);
                      setFilters((prev) => ({
                        ...prev,
                        page: "1",
                        location: option?.value || "",
                      }));
                    }}
                    isClearable
                    placeholder="Select Location"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      menu: (base) => ({
                        ...base,
                        borderRadius: "15px",
                        marginTop: "8px",
                        backgroundColor: "var(--mmp-surface)",
                        padding: "6px",
                        zIndex: "1000",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--mmp-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--mmp-text-secondary)",
                      }),
                      option: (base, state) => ({
                        ...base,
                        // Match the trigger control font size (13px) so the
                        // portalled menu doesn't inherit the body's 16px.
                        fontSize: "13px",
                        lineHeight: 1.3,
                        borderRadius: "8px",
                        padding: "8px 10px",
                        backgroundColor: state.isFocused ? "var(--mmp-hover-bg)" : "var(--mmp-surface)",
                        color: "var(--mmp-text-primary)",
                        cursor: "pointer",
                        "&:active": {
                          backgroundColor: "var(--mmp-hover-bg)",
                        },
                      }),
                    }}
                  />
                </Form.Group>
              </div>
              <div className=" dash-filter">
                <Form.Group className="form-group">
                  <Form.Label className="col-label">
                    Filter by State
                  </Form.Label>

                  <Select
                    aria-label="Filter by state"
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    options={stateOptions}
                    value={
                      filters.state
                        ? { value: filters.state, label: filters.state }
                        : null
                    }
                    onChange={(option) => {
                      setJobs([]);
                      setFilters((prev) => ({
                        ...prev,
                        page: "1",
                        state: option?.value || "",
                      }));
                    }}
                    isClearable
                    placeholder="Select State"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      menu: (base) => ({
                        ...base,
                        borderRadius: "15px",
                        marginTop: "8px",
                        backgroundColor: "var(--mmp-surface)",
                        padding: "6px",
                        zIndex: "1000",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "var(--mmp-text-primary)",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--mmp-text-secondary)",
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: "13px",
                        lineHeight: 1.3,
                        borderRadius: "8px",
                        padding: "8px 10px",
                        backgroundColor: state.isFocused ? "var(--mmp-hover-bg)" : "var(--mmp-surface)",
                        color: "var(--mmp-text-primary)",
                        cursor: "pointer",
                        "&:active": {
                          backgroundColor: "var(--mmp-hover-bg)",
                        },
                      }),
                    }}
                  />
                </Form.Group>
              </div>
              <div className="filter-section">
                <div className="inner-sub-sub-title">
                  <div className="inner-sub-title">Job Type</div>
                </div>
                {JOB_TYPE_OPTIONS.map((workType, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`worktype-${workType.value}`}
                    label={workType.label}
                    checked={filters.workTypes.includes(workType.value)}
                    onChange={() => handleWorkTypeChange(workType.value)}
                  />
                ))}
              </div>
              <div className="filter-section">
                <div className="inner-sub-sub-title">
                  <div className="inner-sub-title">Shift type</div>
                </div>
                {SHIFT_TYPE_OPTION.map((type, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`experience-${type.value}`}
                    label={type.label}
                    checked={filters.shiftType.includes(type.value)}
                    onChange={() => handleShiftType(type.value)}
                  />
                ))}
              </div>

              <div className="filter-section">
                <h6>Work mode</h6>
                {SPECIALTY_TYPE_OPTION.map((spec, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`specialty-${spec.value}`}
                    label={spec.label}
                    checked={filters.specialties.includes(spec.value)}
                    onChange={() => handleWorkMode(spec.value)}
                  />
                ))}
              </div>

              <div className="filter-section">
                <h6>Sort By</h6>
                {sort_by.map((level, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`experience-${level.value}`}
                    label={level.label}
                    checked={filters.sort_by.includes(level.value)}
                    onChange={() => handleSortBy(level.value)}
                  />
                ))}
              </div>
            </div>
          </Card>
        </div>

        <div className="jobs-content">
          <div className="jobs-container">
            <div className="mmp-jobs-topbar">
              {/* Always rendered so switching tabs doesn't unmount the
                  search bar and jump the tabs upward. Search works on both
                  tabs — it filters the live job list or the user's applied
                  jobs (by title / hospital) depending on the active tab. */}
              <div className="mmp-search-input" data-tour="jobs-search">
                <FiSearch className="search-glyph" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={
                    activeTab === "applied"
                      ? "Search your applied jobs..."
                      : "Search by job title or hospital..."
                  }
                  aria-label="Search jobs"
                />
                {/* Bookmark toggle is a Jobs-tab concept (saved listings),
                    so keep it only there. */}
                {activeTab !== "applied" && (
                  <button
                    type="button"
                    className={`bookmark-toggle ${showBookmarks ? "is-active" : ""}`}
                    data-tour="jobs-save-top"
                    onClick={handleBookmarkToggle}
                    aria-label="Toggle bookmarked jobs"
                    aria-pressed={showBookmarks}
                  >
                    {showBookmarks ? (
                      <BookmarkCheck size={18} />
                    ) : (
                      <Bookmark size={18} />
                    )}
                  </button>
                )}
              </div>

              <div className="mmp-jobs-tabs-card">
                <div
                  className="mmp-jobs-tabs"
                  role="tablist"
                  aria-label="Jobs view"
                  data-active={activeTab}
                  data-tour="jobs-tabbar"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "jobs"}
                    className={`tab-btn ${activeTab === "jobs" ? "active" : ""}`}
                    data-tour="jobs-tab-jobs"
                    onClick={() => handleTabChange("jobs")}
                  >
                    Jobs
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "applied"}
                    className={`tab-btn ${activeTab === "applied" ? "active" : ""}`}
                    data-tour="jobs-tab-applied"
                    onClick={() => handleTabChange("applied")}
                  >
                    Applied Jobs
                  </button>
                </div>
              </div>

              <div className="mmp-results-row">
                {isLoading || awaitingResults ? (
                  <p className="results-count">
                    <span className="results-count-loading">
                      <span className="jsk jsk--xs jsk-count-bar" />
                      Loading results…
                    </span>
                  </p>
                ) : hasFeaturedSection ? (
                  <h2 className="mmp-section-title">Featured jobs</h2>
                ) : (
                  <p className="results-count">
                    Showing <b className="tnum">{totalJobs}</b> Results
                  </p>
                )}

                <div className="mmp-results-row__actions">
                  {hasFeaturedSection && !isLoading && !awaitingResults && (
                    <span className="mmp-section-count">
                      {featuredTotal} featured
                    </span>
                  )}
                  {/* Filters apply to the Jobs list only — hide the mobile
                      filter toggle on the Applied tab. */}
                  {activeTab !== "applied" && (
                    <button
                      type="button"
                      className="filter-trigger"
                      data-tour="jobs-filter"
                      onClick={() => setShowFilters(!showFilters)}
                      aria-label="Open filters"
                    >
                      <FiSliders />
                      {activeFilterCount > 0 && (
                        <span
                          className="filter-badge"
                          aria-label={`${activeFilterCount} active filters`}
                        >
                          {activeFilterCount}
                        </span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Job Listings Grid */}
            <Row className="jobs-grid mt-0 g-2">
              {/* NEW: Main loader when page 1 — skeleton cards in place of spinner */}
              {(isLoading || awaitingResults) &&
                (activeTab === "jobs" ? Number(filters.page) === 1 : true) ? (
                <>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <JobCardSkeleton key={i} />
                  ))}
                </>
              ) : (
                <>
                  {/* Job Cards */}
                  {activeTab === "applied"
                    ? jobStatusItems?.applications?.map((application) => (
                      <Col
                        key={application.application_id}
                        xl={6}
                        lg={6}
                        md={6}
                        sm={12}
                        className="simple-job-card"
                      >
                        <AppliedJobCard application={application} />
                      </Col>
                    ))
                    : hasFeaturedSection ? (
                      <>
                        {/* Featured jobs first */}
                        {featuredJobs.map((job, index) => (
                          <Col
                            key={`featured-${job.id}-${index}`}
                            xl={6}
                            lg={6}
                            md={6}
                            sm={12}
                            className="simple-job-card"
                          >
                            <SimpleJobCard
                              job={job}
                              categories={categories}
                              categoriesLoading={categoriesLoading}
                              showBookmarks={showBookmarks}
                              selectedRole={filters.title}
                              selectedJobType={filters.workTypes}
                              selectedShiftType={filters.shiftType}
                              isAppliedJob={false}
                              tour={index === 0}
                            />
                          </Col>
                        ))}

                        {/* Section header before the remaining regular jobs */}
                        <Col xs={12} className="mmp-section-header">
                          <h2 className="mmp-section-title">All jobs</h2>
                          <span className="mmp-section-count">
                            {regularTotal} results
                          </span>
                        </Col>

                        {/* Remaining non-featured jobs */}
                        {regularJobs.map((job, index) => (
                          <Col
                            key={`job-${job.id}-${index}`}
                            xl={6}
                            lg={6}
                            md={6}
                            sm={12}
                            className="simple-job-card"
                          >
                            <SimpleJobCard
                              job={job}
                              categories={categories}
                              categoriesLoading={categoriesLoading}
                              showBookmarks={showBookmarks}
                              selectedRole={filters.title}
                              selectedJobType={filters.workTypes}
                              selectedShiftType={filters.shiftType}
                              isAppliedJob={false}
                              tour={false}
                            />
                          </Col>
                        ))}
                      </>
                    ) : (
                      jobs.map((job, index) => (
                        <Col
                          key={`job-${job.id}-${index}`}
                          xl={6}
                          lg={6}
                          md={6}
                          sm={12}
                          className="simple-job-card"
                        >
                          <SimpleJobCard
                            job={job}
                            categories={categories}
                            categoriesLoading={categoriesLoading}
                            showBookmarks={showBookmarks}
                            selectedRole={filters.title}
                            selectedJobType={filters.workTypes}
                            selectedShiftType={filters.shiftType}
                            isAppliedJob={activeTab === "applied"}
                            tour={index === 0}
                          />
                        </Col>
                      ))
                    )}

                  {isLoadMore && (
                    <Col xs={12} className="d-flex justify-content-center py-4">
                      <Spinner animation="border" variant="primary" />
                    </Col>
                  )}

                  {/* Error state */}
                  {isError && !isLoading && (
                    <Col xs={12} className="text-center py-5">
                      <p className="text-danger mb-2">
                        {typeof errorMessage === "string"
                          ? errorMessage
                          : errorMessage?.message ||
                          "Something went wrong while loading jobs."}
                      </p>
                    </Col>
                  )}

                  {noResults && (
                    <Col xs={12} className="text-center py-5">
                      <h5 className="mb-2">No jobs found</h5>
                      <p className="text-muted">
                        Try adjusting your filters or search keywords.
                      </p>
                    </Col>
                  )}
                </>
              )}
            </Row>
          </div>
        </div>
      </Row>

      {/* Scroll Popup Modal */}
      {showScrollPopup && (
        <div className={`scroll-popup ${showScrollPopup ? "visible" : ""}`}>
          <div className="popup-inner">
            <div className="popup-header">
              <h3 className="popup-title">
                {/* Upgrade to Pro */}
                Unlock More Job Insights
              </h3>
              <button
                className="popup-close"
                onClick={() => {
                  setShowScrollPopup(false);
                  setHasClosedPopup(true);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <p className="popup-subtext">
              Unlock detailed job insights, salary information, hiring trends, and premium tools to discover better job opportunities faster.
            </p>

            <div className="popup-pro-section">
              {hasUsedProTrial ? (
                <>
                  <p className="popup-pro-title">Pro Plan</p>
                  <p className="popup-free-text">$29.99/year</p>
                  <p className="popup-no-charge">Salary comparisons, cost of living filters, investment tools &amp; job data.</p>
                  <div className="popup-price-divider" />
                  <p className="popup-pro-title">Pro + Newsletter</p>
                  <p className="popup-yearly-price">$49.99/year</p>
                  <p className="popup-charge-note">Everything in Pro plus the MapMyPay Insider weekly newsletter.</p>
                </>
              ) : (
                <>
                  <p className="popup-pro-title">Start your 3-day Pro trial</p>
                  <p className="popup-free-text">FREE for 3 days</p>
                  <p className="popup-no-charge">No charge today</p>
                  <div className="popup-price-divider" />
                  <p className="popup-yearly-price">$29.99/year</p>
                  <p className="popup-charge-note">charged on day 4 unless canceled</p>
                </>
              )}
            </div>

            <div className="popup-actions">
              <button
                className="refer-btn"
                onClick={() => {
                  setShowScrollPopup(false);
                  setHasClosedPopup(true);
                  navigate("/referral");
                }}
              >
                Refer & Earn Pro
              </button>

              <button
                className="subscribe-btn"
                onClick={() => {
                  const session_id = sessionStorage.getItem(
                    "comparison_session_id",
                  );

                  if (session_id) {
                    dispatch(
                      jobTracking({
                        event_type: "upgrade_clicked",
                        session_id,
                        timestamp: new Date().toISOString(),
                      }),
                    );
                  }

                  setShowScrollPopup(false);
                  setHasClosedPopup(true);
                  navigate("/profile/subscription", {
                    state: { source: "jobs_page_scroll" },
                  });
                }}
              >
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}

      <GuidedTour
        isOpen={isTourOpen}
        steps={jobsTourSteps}
        onDone={finishTour}
      />
    </div>
  );
};

export default Jobs;
